import React, { useState } from 'react';
import Swal, { SweetAlertOptions } from 'sweetalert2';
import FileSaver from 'file-saver';
import autoTable from 'jspdf-autotable';
import axios from 'axios';
import { Workbook  } from 'exceljs';
import { jsPDF } from 'jspdf';

import { StateLanguage } from '../../languages/config/StateLanguage';
import { Company } from '../../models/company.model';
import { Order } from '../../models/order.model';
import { OrderGroup } from '../../models/order.group.model';
import { legendInvalid, legendValidInvalidRestart } from '../../tools/legend.data.entry.tool';
import { modalHide } from '../../tools/modal.tool';
import { dateFormatAbbreviated } from '../../scripts/datetime.format.script';
import { accumulatedAmountDocuments } from '../../scripts/computed.amount.script';
import { nameGroup } from '../../libraries/group.name.library';
import { addressFormat, contactFormat } from '../../scripts/format.value.script';
import { statusDocumentValue } from '../../libraries/status.document.library';

import AppInputExportType from '../element/InputExportType';
import logo from '../../assets/images/svg/logo-official-dark.png';

export interface AppModalOrderExportProps {
  companyForUser: Company | null,
  grouped: {state: boolean, desc: string},
  orders: Order[] | null,
  orderGroups: OrderGroup[] | null
};

const AppModalOrderExport: React.FunctionComponent<AppModalOrderExportProps> = ({companyForUser, grouped, orders, orderGroups}) => {
  const {lang} = StateLanguage()

  const [loadIndicator, setLoadIndicator] = useState('off')
  const [formatExport, setFormatExport] = useState({value: '', valid: false})

  const fileExcel = async (company: Company, tableTitle: string[][], tableData: any[][]) => {
    let workbook = new Workbook()
    let worksheet = workbook.addWorksheet('Orders Data')

    worksheet.addRow([])
    worksheet.mergeCells(`A1:L1`)
    worksheet.getRow(1).height = 35

    let imageBuffer = await axios.get(logo, {responseType: 'arraybuffer'})

    let imageLogotipo = workbook.addImage({
      buffer: imageBuffer.data,
      extension: 'png'
    })

    worksheet.addImage(imageLogotipo, { tl: {col: 0.1, row: 0.2}, ext: {width: 100, height: 30}})

    let titleRow = worksheet.addRow([`${company.name} - ${lang.labels.companyListOfOrders}`])
    titleRow.font = {size: 18, bold: true}
    titleRow.getCell(1).alignment = {vertical:'distributed', horizontal: 'center'}

    worksheet.mergeCells(`A${titleRow.number}:L${titleRow.number}`)
    worksheet.getRow(2).height = 30

    worksheet.addRow([])
    worksheet.mergeCells(`A3:L3`)
    worksheet.getRow(3).height = 10

    let headerRow = worksheet.addRow(tableTitle[0])
    headerRow.font = {bold: true, color: {argb: 'FFFFFF'}}
    headerRow.eachCell( (cell) => {
      cell.alignment = {vertical:'distributed', horizontal: 'left'}
      cell.fill = {type: 'pattern', pattern: 'solid', fgColor: {argb: '2980BA'}, bgColor: {argb: '2980BA'}}
    })

    worksheet.getRow(4).height = 20

    tableData.forEach((item) => { 
      let bodyRow = worksheet.addRow(item) 

      if (grouped.state) {
        if (item.length === 1) {
          bodyRow.font = {bold: true}
        }
      }
    })

    if (grouped.state) {
      for (let i = 0; i < tableData.length; i++) {
        if (tableData[i].length === 1) {
          worksheet.mergeCells(`A${i + 5}:L${i + 5}`)
        }
      }
    }

    worksheet.getColumn(1).width = 18;
    worksheet.getColumn(2).width = 30;
    worksheet.getColumn(3).width = 25;
    worksheet.getColumn(4).width = 35;
    worksheet.getColumn(5).width = 13;
    worksheet.getColumn(6).width = 13;
    worksheet.getColumn(7).width = 13;
    worksheet.getColumn(8).width = 13;
    worksheet.getColumn(9).width = 15;
    worksheet.getColumn(10).width = 15;
    worksheet.getColumn(11).width = 15;
    worksheet.getColumn(12).width = 17;

    workbook.xlsx.writeBuffer().then( data => {
      let blob = new Blob([data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
      FileSaver.saveAs(blob, `Orders_of_${company.name.replaceAll(' ', '_')}.xlsx`);
    })
  }

  const filePdf = async (company: Company, tableTitle: string[][], tableData: any[][]) => {
    let document = new jsPDF('landscape', 'mm', [340, 220])

    if (grouped.state) {
      for (let i = 0; i < tableData.length; i++) {
        if (tableData[i].length === 1) {
          tableData[i][0] = {colSpan: 10, content: tableData[i][0], styles: {fontStyle: 'bold'}}
        } else {
          tableData[i][8] = {content: tableData[i][8], styles: {halign: 'right'}}
          tableData[i][9] = {content: tableData[i][9], styles: {halign: 'right'}}
          tableData[i][10] = {content: tableData[i][10], styles: {halign: 'right'}}
          tableData[i][11] = {content: tableData[i][11], styles: {halign: 'right'}}
        }
      }
    } else {
      for (let i = 0; i < tableData.length; i++) {
        tableData[i][8] = {content: tableData[i][8], styles: {halign: 'right'}}
        tableData[i][9] = {content: tableData[i][9], styles: {halign: 'right'}}
        tableData[i][10] = {content: tableData[i][10], styles: {halign: 'right'}}
        tableData[i][11] = {content: tableData[i][11], styles: {halign: 'right'}}
      }
    }

    document.addImage(logo, 'PNG', 290, 5, 30, 9)

    document.setFontSize(18)
    document.setFont('helvetica', 'bold')
    document.text(`${company.name} - ${lang.labels.companyListOfOrders}`, 25, 20)

    autoTable(document, {head: tableTitle, body: tableData, startY: 25})

    document.save(`Orders_of_${company.name.replaceAll(' ', '_')}.pdf`)
  }

  const executeOrderExport = () => {
    setLoadIndicator('on')

    if (companyForUser && orders && orderGroups && formatExport.valid) {
      let tableTitle: string[][] = [[lang.labels.orderNumber.toUpperCase(), lang.labels.concerning.toUpperCase(), lang.labels.customer.toUpperCase(), lang.labels.deliveyAddress.toUpperCase(), lang.labels.status.toUpperCase(), lang.labels.creation.toUpperCase(), lang.labels.expiration.toUpperCase(), lang.labels.currency.toUpperCase(), lang.labels.subTotal.toUpperCase(), lang.labels.rebateTotal.toUpperCase(), lang.labels.taxTotal.toUpperCase(), lang.labels.amountTotal.toUpperCase()]]
      let tableData: any[][]  = [[]]

      tableData.pop()

      if (grouped.state) {
        for (let i = 0; i < orderGroups.length; i++) {
          tableData.push([`GROUP ${i + 1}: ${nameGroup(lang, grouped.desc, orderGroups[i].group_detail).toUpperCase()}`])
          for (let order of orderGroups[i].orders) {
            tableData.push([order.order_number, order.concerning, contactFormat(order.customer), addressFormat(lang.code, order.delivery_address), statusDocumentValue(lang, order.status), dateFormatAbbreviated(lang.code, order.created_date), dateFormatAbbreviated(lang.code, order.valid_until), `${order.currency.name} (${order.currency.symbol})`, Number(order.amount.sub_total.toFixed(2)), Number(order.amount.rebate_total.toFixed(2)), Number(order.amount.tax_total.toFixed(2)), Number(order.amount.total.toFixed(2))])
          }
          tableData.push(['', '', '', '', '', '', '', '', Number(accumulatedAmountDocuments(orderGroups[i].orders).subtotal.toFixed(2)), Number(accumulatedAmountDocuments(orderGroups[i].orders).rebate.toFixed(2)), Number(accumulatedAmountDocuments(orderGroups[i].orders).tax.toFixed(2)), Number(accumulatedAmountDocuments(orderGroups[i].orders).total.toFixed(2))])
        }
      } else {
        for (let order of orders) {
          tableData.push([order.order_number, order.concerning, contactFormat(order.customer), addressFormat(lang.code, order.delivery_address), statusDocumentValue(lang, order.status), dateFormatAbbreviated(lang.code, order.created_date), dateFormatAbbreviated(lang.code, order.valid_until), `${order.currency.name} (${order.currency.symbol})`, Number(order.amount.sub_total.toFixed(2)), Number(order.amount.rebate_total.toFixed(2)), Number(order.amount.tax_total.toFixed(2)), Number(order.amount.total.toFixed(2))])
        }
        tableData.push(['', '', '', '', '', '', '', '', Number(accumulatedAmountDocuments(orders).subtotal.toFixed(2)), Number(accumulatedAmountDocuments(orders).rebate.toFixed(2)), Number(accumulatedAmountDocuments(orders).tax.toFixed(2)), Number(accumulatedAmountDocuments(orders).total.toFixed(2))])
      }

      setTimeout( () => {
        switch (formatExport.value) {
          case 'excel':
            fileExcel(companyForUser, tableTitle, tableData)
            break;
          case  'pdf':
            filePdf(companyForUser, tableTitle, tableData)
            break;
        }

        setLoadIndicator('off')
        executeHideModalOrderExport()
      }, 1000)
    } else {
      Swal.fire({
        text: lang.labels.sorryLooksLikeThereAreSomeErrorsTrySolve,
        icon: 'error',
        showConfirmButton: false,
        timer: 1800
      } as SweetAlertOptions).then( () => {
        legendInvalid('modal-order-export-legend-format-export-type-required')
        setLoadIndicator('off')
      })
    }
  }

  const executeHideModalOrderExport = () => {
    modalHide('modal-order-export')

    setTimeout( () => {
      restartModal()
    }, 200 )
  }

  function restartModal() {
    legendValidInvalidRestart('modal-order-export-legend-format-export-type-required')
    setFormatExport({value: '', valid: false})
  }

  return (
    <div id="modal-order-export" className="modal fade" tabIndex={-1} aria-hidden="true" data-bs-backdrop="static" data-bs-keyboard="false">
      <div className="modal-dialog modal-md">
        <div className="modal-content">
          <div className="modal-header">
            <h2 className="text-capitalize">{lang.labels.exportOrders}</h2>
          </div>
          <div className="modal-body">
            <div className="form">
              <div className="fv-row">
                <label className="form-label required">{lang.labels.selectExportFormat}:</label>
                <AppInputExportType component={"modal-order-export"} formatExport={formatExport} setFormatExport={setFormatExport}></AppInputExportType>
              </div>
            </div>
          </div>
          <div className="modal-footer flex-center">
            <button className="btn btn-light mx-2" type="reset" onClick={executeHideModalOrderExport}>{lang.labels.discard}</button>
            <button className="btn btn-primary mx-2" type="button" data-kt-indicator={loadIndicator} onClick={executeOrderExport}>
              <span className="indicator-label">{lang.labels.export}</span>
              <span className="indicator-progress">
                {lang.labels.pleaseWait}
                <span className="spinner-border spinner-border-sm align-middle ms-2"></span>
              </span>
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default AppModalOrderExport;
